import React, { useRef, useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import Button from 'components/Button';
import Card from 'components/Card';
import Loader from 'components/ContentLoader/GenericLoader';
import Spinner from 'components/Spinner';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import Radio from 'components/Radio';
import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';

import { Container, Content, Exibidoras, Form, FormGroups } from './styles';
import api from 'services/api';
import { Emissora } from 'pages/@types';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { show } from 'store/modules/dialog/actions';
import { TipoSolicitacaoChamada } from 'utils/variables';
import { IRootState } from 'store/modules/rootReducer';
interface IOption {
  id: string;
  label: string;
}

// configuração do validator
const schema = Yup.object().shape({
  dataexibicao: Yup.date().typeError('Data inválida'),
  comunicador: Yup.string().required('Campo Obrigatório'),
  chamadagravada: Yup.string()
    .required('Campo Obrigatório')
    .nullable(),
  caminhochamada: Yup.string().when('chamadagravada', {
    is: chamadagravada => chamadagravada === 'sim',
    then: Yup.string().required('Campo Obrigatório'),
  }),
  disponibilidadematerial: Yup.date().typeError('Data inválida'),
  caminhomaterial: Yup.string().required('Campo Obrigatório'),
  caminhobrutas: Yup.string().required('Campo Obrigatório'),
  comentarios: Yup.string().required('Campo Obrigatório'),
  programa: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[^<>:"/\\|?*]+$/, 'O nome contém caracteres inválidos.')
    .nullable(),

  // Jornalismo & Especiais de Entretenimento
  emissora: Yup.string().when('$tipo', {
    is: tipo =>
      tipo === TipoSolicitacaoChamada.ESPECIAIS ||
      tipo === TipoSolicitacaoChamada.JORNALISMO,
    then: Yup.string().required('Escolha pelo menos uma emissora'),
  }),
  dataestreia: Yup.date().when(['$tipo', 'serie'], {
    is: (tipo, serie) =>
      tipo === TipoSolicitacaoChamada.ESPECIAIS ||
      (tipo === TipoSolicitacaoChamada.JORNALISMO && serie === 'sim'),
    then: Yup.date()
      .required('Campo Obrigatório')
      .typeError('Data inválida'),
  }),

  // Jornalismo
  serie: Yup.string().when('$tipo', {
    is: tipo => tipo === TipoSolicitacaoChamada.JORNALISMO,
    then: Yup.string()
      .required('Campo Obrigatório')
      .nullable(),
  }),
  nomeserie: Yup.string().when(['$tipo', 'serie'], {
    is: (tipo, serie) =>
      tipo === TipoSolicitacaoChamada.JORNALISMO && serie === 'sim',
    then: Yup.string().required('Campo Obrigatório'),
  }),
  numepisodio: Yup.number().when(['qtepisodios'], qtepisodios => {
    return Yup.number().max(
      qtepisodios,
      'Episódio maior que o número de episódios',
    );
  }),

  // Programas de Linha (Estúdio C + Plug)
  materiaadicional: Yup.string().when('$tipo', {
    is: tipo => tipo === TipoSolicitacaoChamada.PGMLINHA,
    then: Yup.string().required('Campo Obrigatório'),
  }),
  merchan: Yup.string().when('$tipo', {
    is: tipo => tipo === TipoSolicitacaoChamada.PGMLINHA,
    then: Yup.string().required('Campo Obrigatório'),
  }),
  acaodepalco: Yup.string().when('$tipo', {
    is: tipo => tipo === TipoSolicitacaoChamada.PGMLINHA,
    then: Yup.string().required('Campo Obrigatório'),
  }),
});

export default function SolicitacaoChamadaStore() {
  // react-router-dom
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [chamadaGravada, setChamadaGravada] = useState(false);
  const [serie, setSerie] = useState(false);
  const [rede, setRede] = useState(false);
  const [emissoras, setEmissoras] = useState<IOption[]>([]);
  // const [initialData, setInitialData] = useState({});
  /**
   * USE PARAMS
   */
  const { tipo } = useParams<{ tipo: string }>();
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { profile } = useSelector((state: IRootState) => state.usuario) as any;

  /**
   * USE REF
   */
  const ref = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */
  const handleRede = useCallback(
    e => {
      e.preventDefault();

      if (ref.current) {
        if (ref.current.getFieldValue('emissora').length === 8)
          ref.current.setFieldValue('emissora', []);
        else
          ref.current.setFieldValue(
            'emissora',
            emissoras.map(e => e.id),
          );

        setRede(ref.current.getFieldValue('emissora').length === 8);
      }
    },
    [emissoras],
  );

  const handleMessage = useCallback(
    ({ contentHTML, redirectTo, type }) => {
      const option = {
        type: type,
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        if (ref.current) {
          setLoadingSubmit(true);

          // Realiza validação de campos obrigatórios
          ref.current.setErrors({});
          await schema.validate(data, {
            abortEarly: false,
            context: { tipo },
          });

          // Envia requisição de criação ou edição para o backend
          const response = await api.post('/solicitacaochamada', {
            ...data,
            emissora:
              tipo === TipoSolicitacaoChamada.PGMLINHA ||
              data.emissora.length === 8
                ? 'REDE'
                : data.emissora.join(),
            ...(data.dataexibicao && {
              dataexibicao: format(data.dataexibicao, 'yyyy-MM-dd'),
            }),
            ...(data.disponibilidadematerial && {
              disponibilidadematerial: format(
                data.disponibilidadematerial,
                'yyyy-MM-dd',
              ),
            }),
            ...(data.dataestreia && {
              dataestreia: format(data.dataestreia, 'yyyy-MM-dd'),
            }),
            chamadagravada: data.chamadagravada === 'sim',
            serie: data.serie === 'sim',
            tipo,
          });

          // Redireciona usuário em caso de sucesso
          if (response && response.data)
            handleMessage({
              contentHTML: `<h3>Solicitação salva!</h3> <p>Você será redirecionado para a lista de solicitações em 3 segundos</p>`,
              redirectTo: `/solicitacaochamada`,
              type: 'success',
            });
          else toast.error('Falha ao salvar a solicitação');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (ref.current) ref.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleMessage, tipo],
  );

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadEmissoras() {
      setLoading(true);
      await api
        .get('/emissoras')
        .then(response => {
          if (response && response.data) {
            const emissorasNormalized = response.data.map((emi: Emissora) => ({
              id: emi.nome,
              label: emi.nome,
            }));
            setEmissoras(emissorasNormalized);
          }
        })
        .catch(error => {
          if (error instanceof Error) {
            toast.error(error.message);
          } else console.log(error);
        });
      setLoading(false);
    }

    loadEmissoras();
  }, []);

  useEffect(() => {
    if (
      tipo !== TipoSolicitacaoChamada.ESPECIAIS &&
      tipo !== TipoSolicitacaoChamada.JORNALISMO &&
      tipo !== TipoSolicitacaoChamada.PGMLINHA
    )
      handleMessage({
        contentHTML: `<h3>Tipo inexistente!</h3> <p>Você será redirecionado para a lista de solicitações em 3 segundos</p>`,
        redirectTo: `/solicitacaochamada`,
        type: 'error',
      });
  }, [handleMessage, tipo]);

  function renderPgmLinha() {
    return (
      <FormGroups $columns="1fr 1fr 1fr">
        <div>
          <section>
            <h5>Tem mais matérias neste dia? Com qual comunicador?</h5>
          </section>
          <TextArea name="materiaadicional" height="70" icon="" errorField="" />
        </div>
        <div>
          <section>
            <h5>Tem merchan ou conteúdos especiais?</h5>
          </section>
          <TextArea name="merchan" height="70" icon="" errorField="" />
        </div>
        <div>
          <section>
            <h5>Qual é a ação de palco?</h5>
          </section>
          <TextArea name="acaodepalco" height="70" icon="" errorField="" />
        </div>
      </FormGroups>
    );
  }
  function renderEspeciais() {
    return (
      <FormGroups $columns="1fr 1fr 1fr">
        <div>
          <section>
            <h5>Data de estreia:</h5>
          </section>
          <DatePicker
            placeholderText="Data de estreia"
            minDate={new Date()}
            name="dataestreia"
            disabled={false}
          />
        </div>
        <div>
          <section>
            <h5>Qual episódio é esta solicitação?</h5>
          </section>
          <Input name="numepisodio" defaultValue={1} type="number" min={1} />
        </div>
        <div>
          <section>
            <h5>Número de episódios</h5>
          </section>
          <Input name="qtepisodios" defaultValue={1} type="number" min={1} />
        </div>
      </FormGroups>
    );
  }
  function renderJornalismo() {
    return (
      <FormGroups $columns="1fr 4fr 2fr 1fr 1fr">
        <div>
          <section>
            <h5>É uma série? *</h5>
          </section>
          <Radio
            name="serie"
            options={[
              { id: 'sim', label: 'SIM' },
              { id: 'nao', label: 'NÃO' },
            ]}
            handleSelected={value => setSerie(value.id === 'sim')}
          />
        </div>
        {serie && (
          <>
            <div>
              <section>
                <h5>Nome da série *</h5>
              </section>
              <Input name="nomeserie" max={100} placeholder="Nome da série *" />
            </div>
            <div>
              <section>
                <h5>Data de estreia: *</h5>
              </section>
              <DatePicker
                placeholderText="Data de estreia *"
                minDate={new Date()}
                name="dataestreia"
                disabled={false}
              />
            </div>
            <div>
              <section>
                <h5>Qual episódio é esta solicitação?</h5>
              </section>
              <Input
                name="numepisodio"
                defaultValue={1}
                type="number"
                min={1}
              />
            </div>
            <div>
              <section>
                <h5>Número de episódios</h5>
              </section>
              <Input
                name="qtepisodios"
                defaultValue={1}
                type="number"
                min={1}
              />
            </div>
          </>
        )}
      </FormGroups>
    );
  }

  return (
    <Container>
      <Card height="auto">
        {loading ? (
          <Loader type="default" />
        ) : (
          <Form ref={ref} onSubmit={handleSubmit}>
            <Content>
              <header>
                <h4>SOLICITAÇÕES - CHAMADAS</h4>
              </header>
              {tipo === TipoSolicitacaoChamada.ESPECIAIS && (
                <div>
                  <section>
                    <h5>Nome do Programa</h5>
                  </section>
                  <Input
                    name="programa"
                    max={45}
                    placeholder="Nome do Programa *"
                  />
                </div>
              )}
              {tipo === TipoSolicitacaoChamada.JORNALISMO && (
                <div>
                  <section>
                    <h5>Programa *</h5>
                  </section>
                  <Radio
                    columns="1fr 1fr 1fr 1fr 1fr"
                    name="programa"
                    options={[
                      { id: 'BOM DIA PARANÁ', label: 'BOM DIA PARANÁ' },
                      { id: 'MEIO DIA PARANÁ', label: 'MEIO DIA PARANÁ' },
                      { id: 'BOA NOITE PARANÁ', label: 'BOA NOITE PARANÁ' },
                      { id: 'BOM DIA SÁBADO', label: 'BOM DIA SÁBADO' },
                      { id: 'GLOBO ESPORTE', label: 'GLOBO ESPORTE' },
                    ]}
                  />
                </div>
              )}
              {tipo === TipoSolicitacaoChamada.PGMLINHA && (
                <div>
                  <section>
                    <h5>Programa *</h5>
                  </section>
                  <Radio
                    columns="1fr 1fr 1fr 1fr 1fr"
                    name="programa"
                    options={[
                      { id: 'ESTÚDIO C', label: 'ESTÚDIO C' },
                      { id: 'PLUG', label: 'PLUG' },
                    ]}
                  />
                </div>
              )}
              {tipo !== TipoSolicitacaoChamada.PGMLINHA && (
                <>
                  <Exibidoras>
                    <h5>Em qual praça será exibido? *</h5>
                    <Button
                      fill
                      color="primary"
                      size="xsm"
                      onClick={e => handleRede(e)}
                    >
                      REDE ? {rede ? 'SIM' : 'NÃO'}
                    </Button>
                  </Exibidoras>
                  <Checkbox
                    name="emissora"
                    options={emissoras}
                    handleSelected={() =>
                      setRede(
                        ref.current?.getFieldValue('emissora').length === 8,
                      )
                    }
                  />
                </>
              )}

              <FormGroups $columns="1fr 3fr">
                <div>
                  <section>
                    <h5>Data que o programa vai ao ar: *</h5>
                  </section>
                  <DatePicker
                    placeholderText="Data de exibição"
                    minDate={new Date()}
                    name="dataexibicao"
                    disabled={false}
                  />
                </div>
                <div>
                  <section>
                    <h5>Quem é o repórter/apresentador desta matéria? *</h5>
                  </section>
                  <Input
                    name="comunicador"
                    max={100}
                    placeholder="Comunicador *"
                  />
                </div>
              </FormGroups>
              <FormGroups $columns="1fr 3fr">
                <div>
                  <section>
                    <h5>Tem chamada gravada? *</h5>
                  </section>
                  <Radio
                    name="chamadagravada"
                    options={[
                      { id: 'sim', label: 'SIM' },
                      { id: 'nao', label: 'NÃO' },
                    ]}
                    handleSelected={value =>
                      setChamadaGravada(value.id === 'sim')
                    }
                  />
                </div>
                {chamadaGravada && (
                  <div>
                    <section>
                      <h5>Qual o caminho da chamada gravada? *</h5>
                    </section>
                    <Input
                      name="caminhochamada"
                      placeholder="Caminho + Retranca *"
                    />
                  </div>
                )}
              </FormGroups>
              <FormGroups $columns="1fr 3fr">
                <div>
                  <section>
                    <h5>
                      Quando o material estará disponível para trabalharmos? *
                    </h5>
                  </section>
                  <DatePicker
                    placeholderText="Disponibilidade *"
                    name="disponibilidadematerial"
                    disabled={false}
                  />
                </div>
                <div>
                  <section>
                    <h5>
                      Indique o caminho e retranca (do projeto, pasta ou
                      arquivo) *
                    </h5>
                  </section>
                  <Input
                    name="caminhomaterial"
                    placeholder="Caminho + Retranca *"
                  />
                </div>
              </FormGroups>
              <div>
                <section>
                  <h5>Onde podemos encontrar mais imagens/material bruto? *</h5>
                </section>
                <Input
                  name="caminhobrutas"
                  placeholder="Caminho + Retranca *"
                />
              </div>
              <div>
                <section>
                  <h5>
                    Preencha aqui com mais informações sobre essa história *
                  </h5>
                </section>
                <TextArea
                  name="comentarios"
                  height="70"
                  icon=""
                  errorField=""
                />
              </div>

              {tipo === TipoSolicitacaoChamada.JORNALISMO
                ? renderJornalismo()
                : tipo === TipoSolicitacaoChamada.ESPECIAIS
                ? renderEspeciais()
                : renderPgmLinha()}

              <div>
                <section>
                  <h5>Telefone para contato</h5>
                </section>
                <Input
                  max={45}
                  name="telefone"
                  defaultValue={profile.telefone || null}
                  placeholder="Telefone"
                />
              </div>
            </Content>
            <div className="ButtonGroup">
              <Button
                color="primary"
                onClick={e => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                <FiArrowLeft />
                VOLTAR
              </Button>
              <div style={{ display: 'flex' }}>
                <Button className="submitButton" type="submit" color="primary">
                  <FiSave />
                  {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Card>
    </Container>
  );
}
